'use client'

import { useState, useMemo, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Play, Upload, Loader2, ExternalLink, Video, ImageIcon, RotateCcw, Trash2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

/** Format duration in seconds to M:SS or H:MM:SS */
function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return ''
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export type PreTripVideoCardProps = {
  title: string
  /** YouTube embed URL, Vimeo embed URL, or direct video URL (from config) */
  url: string
  /** Uploaded video URL from API (takes precedence over url when set) */
  videoUrl?: string | null
  /** Thumbnail image URL for uploaded video (capture frame; do not load full video) */
  videoThumbnailUrl?: string | null
  /** Duration in seconds for uploaded video (optional badge) */
  videoDuration?: number | null
  /** When set, show Upload/Replace video and call with selected file */
  onUploadVideo?: (file: File, options?: { keepCustomPoster?: boolean }) => void
  isUploadingVideo?: boolean
  /** Uploaded PreTrip video only: custom poster (same thumbnailUrl row) */
  onUploadPoster?: (file: File) => void
  isUploadingPoster?: boolean
  onRevertPoster?: () => void
  isRevertingPoster?: boolean
  /** When true, show loading state in the video area (e.g. while pretrip data is fetching) */
  isLoadingPreTrip?: boolean
  /** When true, on desktop (md+) the video area has a max height so the card is shorter vertically (e.g. Volunteer Groups PreTrip) */
  compactHeightOnDesktop?: boolean
  replaceVideoDialogTitle?: string
  replaceVideoDialogDescription?: string
  /** Admin-only: permanently delete uploaded video from storage */
  onDeleteVideo?: () => void | Promise<void>
  isDeletingVideo?: boolean
}

function isYouTubeEmbedUrl(url: string): boolean {
  return /youtube\.com\/embed\/|youtu\.be\//.test(url)
}

function isVimeoEmbedUrl(url: string): boolean {
  return /player\.vimeo\.com\/video\//.test(url)
}

/** Normalize YouTube watch URL to embed URL if needed */
function toEmbedUrl(url: string): string {
  const trimmed = url.trim()
  const ytMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
  return trimmed
}

export function PreTripVideoCard({
  title,
  url,
  videoUrl = null,
  videoThumbnailUrl = null,
  videoDuration = null,
  onUploadVideo,
  isUploadingVideo = false,
  onUploadPoster,
  isUploadingPoster = false,
  onRevertPoster,
  isRevertingPoster = false,
  isLoadingPreTrip = false,
  compactHeightOnDesktop = false,
  replaceVideoDialogTitle = 'Replace PreTrip video?',
  replaceVideoDialogDescription = 'Choose what happens to the poster image shown before play. You can also change the poster anytime after upload.',
  onDeleteVideo,
  isDeletingVideo = false,
}: PreTripVideoCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const posterInputRef = useRef<HTMLInputElement>(null)
  const [showInlinePlayer, setShowInlinePlayer] = useState(false)
  const [replaceVideoDialogOpen, setReplaceVideoDialogOpen] = useState(false)
  const [deleteVideoDialogOpen, setDeleteVideoDialogOpen] = useState(false)
  const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null)
  const configUrl = url?.trim() || ''
  const embedUrl = useMemo(() => toEmbedUrl(configUrl), [configUrl])
  const isConfigEmbed = useMemo(
    () => isYouTubeEmbedUrl(embedUrl) || isVimeoEmbedUrl(embedUrl),
    [embedUrl]
  )
  const hasUploadedVideo = Boolean(videoUrl?.trim())
  const hasConfigUrl = Boolean(configUrl)
  const playUrl = hasUploadedVideo ? videoUrl! : embedUrl
  const hasVideo = hasUploadedVideo || hasConfigUrl
  const isEmbed = hasUploadedVideo ? false : isConfigEmbed

  const openFileDialog = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isUploadingVideo || !onUploadVideo) return
    setTimeout(() => fileInputRef.current?.click(), 0)
  }

  const openPosterFileDialog = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isUploadingPoster || !onUploadPoster) return
    setTimeout(() => posterInputRef.current?.click(), 0)
  }

  const flushPendingVideo = () => {
    setPendingVideoFile(null)
    setReplaceVideoDialogOpen(false)
  }

  const confirmReplaceVideo = (keepCustomPoster: boolean) => {
    if (!pendingVideoFile || !onUploadVideo) return
    onUploadVideo(pendingVideoFile, { keepCustomPoster })
    flushPendingVideo()
  }

  const videoAreaClass = cn(
    'w-full aspect-video rounded-lg overflow-hidden bg-muted',
    compactHeightOnDesktop && 'md:max-h-[360px]'
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Play className="h-5 w-5 shrink-0" />
            {title}
          </CardTitle>
          {hasVideo && playUrl && (
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <a
                href={playUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5"
              >
                <ExternalLink className="h-4 w-4" />
                Play in new tab
              </a>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {onUploadVideo && (
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
            className="sr-only"
            disabled={isUploadingVideo}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                if (hasUploadedVideo) {
                  setPendingVideoFile(file)
                  setReplaceVideoDialogOpen(true)
                } else {
                  onUploadVideo(file)
                }
                e.target.value = ''
              }
            }}
          />
        )}
        {onUploadPoster && (
          <input
            ref={posterInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={isUploadingPoster}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                onUploadPoster(file)
                e.target.value = ''
              }
            }}
          />
        )}
        <Dialog open={replaceVideoDialogOpen} onOpenChange={(open) => !open && flushPendingVideo()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{replaceVideoDialogTitle}</DialogTitle>
              <DialogDescription>
                {replaceVideoDialogDescription}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={flushPendingVideo}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => confirmReplaceVideo(true)}
              >
                Keep current poster
              </Button>
              <Button type="button" onClick={() => confirmReplaceVideo(false)}>
                Use new frame from video
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={deleteVideoDialogOpen} onOpenChange={setDeleteVideoDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this video permanently?</DialogTitle>
              <DialogDescription>
                This removes the video file and poster from storage. Viewers will no longer see this clip for{' '}
                <span className="font-medium text-foreground">{title}</span>. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDeleteVideoDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isDeletingVideo}
                onClick={async () => {
                  try {
                    await Promise.resolve(onDeleteVideo?.())
                    setDeleteVideoDialogOpen(false)
                  } catch {
                    /* parent surfaced error; leave dialog open */
                  }
                }}
              >
                {isDeletingVideo ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  'Delete permanently'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {isLoadingPreTrip ? (
          <div className={cn(
            'rounded-lg border border-dashed bg-muted/50 p-8 text-center text-muted-foreground',
            compactHeightOnDesktop && 'md:max-h-[360px] md:py-6 md:flex md:flex-col md:items-center md:justify-center'
          )}>
            <Loader2 className="mx-auto h-10 w-10 animate-spin opacity-70" />
            <p className="mt-2 text-sm font-medium">Loading video…</p>
          </div>
        ) : !hasVideo ? (
          <div className={cn(
            'rounded-lg border border-dashed bg-muted/50 p-8 text-center text-muted-foreground',
            compactHeightOnDesktop && 'md:max-h-[360px] md:py-6'
          )}>
            <Play className="mx-auto h-10 w-10 opacity-50" />
            <p className="mt-2 text-sm font-medium">No video</p>
            <p className="text-xs">Upload a video (MP4, MOV, WebM, or M4V) to play here.</p>
            {onUploadVideo && (
              <button
                type="button"
                onClick={openFileDialog}
                disabled={isUploadingVideo}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer disabled:opacity-70"
              >
                {isUploadingVideo ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload video
                  </>
                )}
              </button>
            )}
          </div>
        ) : isEmbed ? (
          <div className={cn('relative', videoAreaClass)}>
            <iframe
              src={playUrl}
              title={title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : showInlinePlayer ? (
          <div className={cn('relative', videoAreaClass, 'border')}>
            <video
              src={playUrl ?? undefined}
              controls
              autoPlay
              className="w-full h-full object-contain"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="absolute top-2 right-2 z-10"
              onClick={() => setShowInlinePlayer(false)}
            >
              Back to thumbnail
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowInlinePlayer(true)}
            className={cn('relative block', videoAreaClass, 'group cursor-pointer border text-left')}
          >
            {videoThumbnailUrl ? (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  key={videoThumbnailUrl}
                  src={videoThumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-opacity pointer-events-none">
                  <div className="relative">
                    <div className="absolute inset-0 bg-white rounded-full opacity-20 blur-md" />
                    <Play className="relative w-12 h-12 text-white fill-white drop-shadow-lg" />
                  </div>
                </div>
                {videoDuration != null && videoDuration > 0 && (
                  <div className="absolute bottom-2 right-2 bg-black/75 text-white text-xs px-2 py-1 rounded font-medium pointer-events-none">
                    {formatDuration(videoDuration)}
                  </div>
                )}
                <div className="absolute top-2 left-2 bg-black/75 text-white p-1.5 rounded pointer-events-none">
                  <Video className="w-4 h-4" />
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground pointer-events-none">
                <Play className="w-12 h-12 mb-2 opacity-70" />
                <span className="text-sm font-medium">Click to play video</span>
              </div>
            )}
          </button>
        )}
        {hasVideo && onUploadVideo && (
          <button
            type="button"
            onClick={openFileDialog}
            disabled={isUploadingVideo}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer disabled:opacity-70 bg-transparent border-0 p-0 font-inherit"
          >
            {isUploadingVideo ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-3.5 w-3.5" /> Replace with another video</>
            )}
          </button>
        )}
        {hasUploadedVideo && onUploadPoster && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={openPosterFileDialog}
              disabled={isUploadingPoster || isRevertingPoster}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer disabled:opacity-70 bg-transparent border-0 p-0 font-inherit"
            >
              {isUploadingPoster ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading poster…</>
              ) : (
                <><ImageIcon className="h-3.5 w-3.5" /> Change poster image</>
              )}
            </button>
            {onRevertPoster && (
              <button
                type="button"
                onClick={onRevertPoster}
                disabled={isUploadingPoster || isRevertingPoster}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:underline cursor-pointer disabled:opacity-70 bg-transparent border-0 p-0 font-inherit"
              >
                {isRevertingPoster ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Resetting…</>
                ) : (
                  <><RotateCcw className="h-3.5 w-3.5" /> Revert to auto poster</>
                )}
              </button>
            )}
          </div>
        )}
        {hasUploadedVideo && onDeleteVideo && (
          <div className="pt-2 border-t border-border/60 mt-2">
            <button
              type="button"
              onClick={() => setDeleteVideoDialogOpen(true)}
              disabled={isUploadingVideo || isUploadingPoster || isRevertingPoster || isDeletingVideo}
              className="inline-flex items-center gap-1.5 text-xs text-destructive hover:underline cursor-pointer disabled:opacity-70 bg-transparent border-0 p-0 font-inherit"
            >
              {isDeletingVideo ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="h-3.5 w-3.5" /> Delete video</>
              )}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
